import { useNavigate } from "react-router-dom";
import {
  Wrench,
  Drill,
  HardHat,
  Cog,
  Users,
  Truck,
  ShieldCheck,
  Package,
  ArrowRight,
} from "lucide-react";

import rigBg from "../../assets/images/offshore_drilling_operations.jpg";
import MotionReveal from "../common/MotionReveal/MotionReveal";
import { ROUTES, SERVICE_ROUTES } from "../../constants/routes";

function ServicesPreview() {
  const navigate = useNavigate();

  const services = [
    {
      title: "Operations & Maintenance",
      icon: Cog,
      description:
        "Integrated maintenance, asset reliability, and operational continuity support.",
      path: SERVICE_ROUTES.om,
      ctaLabel: "View O&M",
    },
    {
      title: "Drilling Support Services",
      icon: Drill,
      description:
        "Operational drilling support for offshore and onshore energy programs.",
      path: SERVICE_ROUTES.drilling,
      ctaLabel: "View Drilling",
    },
    {
      title: "Workover & Well Intervention",
      icon: Wrench,
      description:
        "Specialized intervention and completion services to restore and optimize wells.",
      path: SERVICE_ROUTES.workover,
      ctaLabel: "View Workover",
    },
    {
      title: "Global Workforce Solutions",
      icon: Users,
      description:
        "Operator-driven workforce planning, mobilization, and crew support solutions.",
      path: ROUTES.GWS,
      ctaLabel: "View GWS",
    },
    {
      title: "HSEQ Management",
      icon: ShieldCheck,
      description:
        "Safety, quality, environmental, and compliance systems built for energy operations.",
      path: ROUTES.HSEQ,
      ctaLabel: "View HSEQ",
    },
    {
      title: "Awards and Recognitions",
      icon: Package,
      description:
        "Certifications, operational achievements, and industry recognitions in one place.",
      path: ROUTES.AWARDS_AND_RECOGNITIONS,
      ctaLabel: "View Awards and Recognitions",
    },
    {
      title: "Project Experience",
      icon: HardHat,
      description:
        "Explore offshore and onshore project execution across India and global markets.",
      path: ROUTES.PROJECTS,
      ctaLabel: "View Projects",
    },
    {
      title: "All Services Overview",
      icon: Truck,
      description:
        "Browse SAC's full service portfolio across drilling, workover, and O&M.",
      path: ROUTES.SERVICES,
      ctaLabel: "View Services",
    },
  ];

  return (
    <section className="relative overflow-hidden py-16 lg:py-20">
      {/* Full Background Image - Increased Visibility */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.55]"
        style={{ backgroundImage: `url(${rigBg})` }}
      />
      
      {/* Light overlay to ensure text readability */}
      <div className="absolute inset-0 bg-white/60" />

      {/* Decorative Blur */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-red-500/5 blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#041E42]/5 blur-[140px] pointer-events-none" />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 relative">
        <div className="grid xl:grid-cols-[0.95fr_1.45fr] gap-20 items-start">
          {/* LEFT CONTENT */}
          <div className="relative">
            <span className="inline-flex items-center gap-4">
              <span className="w-10 h-[2px] bg-red-600 rounded-full" />
              <span className="uppercase tracking-[0.45em] text-xs font-semibold text-red-600">
                What We Do
              </span>
            </span>

            <h2 className="mt-8 text-[3rem] sm:text-[3.6rem] xl:text-[5rem] leading-[0.95] font-bold tracking-tight text-[#041E42]">
              Comprehensive
              <br />
              Solutions
              <br />
              Across the
              <span className="block mt-3 text-red-600">
                Operational
                <br />
                Lifecycle
              </span>
            </h2>

            <p className="mt-10 max-w-[520px] text-slate-600 text-lg leading-9 text-justify">
              South Asia Consultancy delivers comprehensive solutions covering
              the complete operational lifecycle of energy assets. By integrating
              operational expertise with workforce excellence, SAC provides
              clients with a single trusted partner capable of supporting
              diverse project requirements across multiple regions and operational
              environments.
            </p>

            <button
              type="button"
              onClick={() => navigate("/services")}
              className="mt-10 inline-flex items-center gap-5 font-semibold text-[#041E42] group"
            >
              <span className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition duration-300">
                <ArrowRight
                  size={20}
                  className="text-white group-hover:translate-x-1 transition-transform duration-300"
                />
              </span>
              <span className="text-lg group-hover:text-red-600 transition-colors">
                Explore Our Services
              </span>
            </button>
          </div>

          {/* RIGHT GRID */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-7">
            {services.map((service, index) => {
              const Icon = service.icon;

              return (
                <MotionReveal variant="fadeUp" as="button" className="group relative overflow-hidden rounded-[30px] bg-white/70 backdrop-blur-sm border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.08)] transition-all duration-500 hover:-translate-y-2 hover:border-red-200 hover:shadow-[0_25px_70px_rgba(15,23,42,0.12)] p-7 min-h-[235px] text-left flex flex-col" key={service.title} type="button" onClick={() => navigate(service.path)}>
                  {/* Top Accent */}
                  <div className="absolute top-0 left-0 h-[4px] w-0 bg-gradient-to-r from-red-600 to-red-400 group-hover:w-full transition-all duration-500" />

                  {/* Background Number */}
                  <span className="absolute right-7 top-5 text-6xl font-bold text-slate-100 group-hover:text-red-50 transition-colors duration-500">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  {/* Icon */}
                  <div className="relative w-20 h-20 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center group-hover:bg-[#041E42] group-hover:border-[#041E42] transition-all duration-500">
                    <Icon
                      size={38}
                      strokeWidth={1.8}
                      className="text-red-600 group-hover:text-white transition-colors duration-500"
                    />
                  </div>

                  {/* Accent */}
                  <div className="mt-7 w-10 h-[3px] rounded-full bg-red-600 group-hover:w-20 transition-all duration-500" />

                  {/* Title */}
                  <h3 className="mt-6 text-[1.18rem] leading-7 font-bold text-[#041E42] transition-colors duration-300 group-hover:text-red-600">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {service.description}
                  </p>

                  <div className="flex-grow" />

                  {/* Bottom CTA */}
                  <div className="mt-8 flex min-w-0 items-center justify-between gap-3">
                    <span className="min-w-0 uppercase tracking-[0.3em] text-[11px] font-semibold leading-snug text-slate-400 break-words">
                      {service.ctaLabel}
                    </span>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100 transition-all duration-300 group-hover:bg-red-600">
                      <ArrowRight
                        size={18}
                        className="text-[#041E42] transition-all duration-300 group-hover:translate-x-1 group-hover:text-white"
                      />
                    </div>
                  </div>
                </MotionReveal>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Fade */}
      <div className="absolute bottom-0 left-0 w-full h-48 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}

export default ServicesPreview;